import React, { useMemo, useState } from "react";
import VehicleIcon from "../../../assets/icons/gocar_icon.svg";
import AssetIcon from "../../../assets/icons/adaptive_speaker.svg";
import { Badge, IconWithTooltip } from "../VehicleManagementGrid.styles";

import { COLORS, TOOLTIP_MESSAGES } from "../VehicleManagementConstants";

const InstallationDetailsTable = ({ data = [], defaultExpanded = false, isVehicle }) => {
  const formattedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((vehicle) => {
      const { subscriptions = [] } = vehicle;
      if (!subscriptions || subscriptions.length === 0) {
        return { ...vehicle, formattedSubscriptions: [] };
      }
      const mappedSubscriptions = subscriptions.reduce((acc, cur) => {
        const subId = cur.subscriptionId;
        if (!acc[subId]) {
          acc[subId] = {
            subscriptionName: cur.subscriptionName,
            subscriptionId: cur.subscriptionId,
            techRefId: cur.techRefId,
            materials: [],
          };
        }
        acc[subId].materials.push({
          ...cur,
        });
        return acc;
      }, {});

      return { ...vehicle, formattedSubscriptions: Object.values(mappedSubscriptions) };
    });
  }, [data]);

  const [expandedRows, setExpandedRows] = useState(() => {
    if (defaultExpanded && formattedData.length > 0) {
      return formattedData.reduce((acc, row, index) => {
        const rowId = row.id || index;
        acc[rowId] = true;
        return acc;
      }, {});
    }
    return {};
  });

  // Define column widths as constants for alignment
  const columnWidths = {
    vehicle: "25%",
    traits: "10%",
    installDetails: "15%",
    shippingAddress: "20%",
    shippingStatus: "15%",
    instStatus: "15%",
  };

  // Child column widths - first 3 columns fit within first 3 parent columns (50%)
  // Last 3 columns align with parent's last 3 columns (50%)
  const childColumnWidths = {
    subscription: "16.67%",
    category: "16.67%",
    hardware: "16.66%",
    shippingAddress: "20%",
    substatus1: "15%",
    substatus2: "15%",
  };

  const styles = {
    tableContainer: {
      width: "100%",
      overflowX: "auto",
      fontFamily: "Verizon-NHG-eDS, Arial, sans-serif",
      border: "1px solid #E0E0E0",
      borderRadius: "9px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
      tableLayout: "fixed",
    },
    th: {
      padding: "12px 8px",
      textAlign: "left",
      fontWeight: 700,
      fontSize: "14px",
      lineHeight: "18px",
      letterSpacing: "0.5px",
      color: "#000",
      borderRight: "1px solid #E0E0E0",
      borderBottom: "1.3px solid #000",
    },
    thLast: {
      borderRight: "none",
    },
    parentRow: {
      cursor: "pointer",
      backgroundColor: "#AAA8A31F",
      padding: "12px 8px",
    },
    parentRowExpanded: {
      backgroundColor: "#AAA8A31F",
    },
    td: {
      padding: "12px 8px",
      textAlign: "left",
      fontSize: "14px",
      color: "#000",
      verticalAlign: "top",
      borderRight: "1px solid #E0E0E0",
      borderBottom: "1px solid #E0E0E0",
    },
    tdLast: {
      borderRight: "none",
    },
    tdExpandedParent: {
      borderBottom: "none",
    },
    vehicleCell: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    vehicleInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    vehicleName: {
      fontWeight: 700,
      fontSize: "12px",
      lineHeight: "16px",
      letterSpacing: "0px",
      textDecoration: "underline",
      textUnderlineOffset: "4px",
      textDecorationThickness: "1px",
      height: "16px",
      marginBottom: "2px",
    },
    vmiBadge: {
      display: "inline-block",
      padding: "2px 8px",
      backgroundColor: "#FFF3E0",
      color: "#E65100",
      fontSize: "11px",
      fontWeight: 600,
      borderRadius: "4px",
    },
    installTypeLabel: { fontWeight: "400", fontSize: "12px", lineHeight: "16px" },
    vehicleDetail: {
      fontSize: "12px",
      lineHeight: "16px",
      color: "#000",
      margin: 0,
      fontWeight: "700",
    },
    traitsCell: {
      fontSize: "14px",
      color: "#000",
      display: "flex",
      flexDirection: "column",
    },
    traitsTitle: {
      fontWeight: "700",
      fontSize: "14px",
      lineHeight: "18px",
      letterSpacing: "0.5px",
      marginBottom: "4px",
    },
    traitsValue: {
      fontWeight: "500",
      fontSize: "12px",
      lineHeight: "1",
      letterSpacing: "2%",
    },
    traitsRow: {
      marginTop: "4px",
      display: "flex",
    },
    installDetails: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    installDetailRow: {
      fontSize: "12px",
      color: "#000",
      lineHeight: "16px",
    },
    installDetailTitle: {
      fontWeight: "700",
    },
    installDetailValue: {
      fontWeight: "400",
      paddingLeft: "2px",
    },
    addressCell: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    addressText: {
      fontSize: "12px",
      color: "#000",
    },
    contactText: {
      fontSize: "12px",
      color: "#000",
    },
    statusCell: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    // Last column cell with expand icon
    lastColumnCell: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "8px",
    },
    statusBadge: {
      display: "inline-block",
      padding: "2px 4px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: "16px",
      width: "fit-content",
    },
    statusShipped: {
      backgroundColor: "#008331",
      color: "#FFF",
    },
    statusNeverScheduled: {
      backgroundColor: "#F5F5F5",
      color: "#6F7171",
      border: "1px solid #D8D8D8",
    },
    deliveryDateTitle: {
      fontSize: "12px",
      color: "#000",
      fontWeight: "700",
      lineHeight: "16px",
    },
    deliveryDateValue: {
      fontSize: "12px",
      color: "#000",
      fontWeight: "500",
      lineHeight: "16px",
    },
    // Child row that spans all columns
    childRowCell: {
      padding: 0,
      backgroundColor: "#fff",
      borderBottom: "1px solid #E0E0E0",
    },
    // Child table - no outer border, aligns with parent
    childTable: {
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "fixed",
    },
    childHeaderRow: {
      backgroundColor: "#fff",
    },
    childTh: {
      padding: "12px 10px",
      textAlign: "left",
      fontWeight: 600,
      fontSize: "12px",
      color: "#333",
      borderRight: "1px solid #E0E0E0",
      borderBottom: "1px solid #E0E0E0",
    },
    childThLast: {
      borderRight: "none",
    },
    childTd: {
      padding: "12px 10px",
      textAlign: "left",
      fontSize: "13px",
      color: "#333",
      verticalAlign: "top",
      borderRight: "1px solid #E0E0E0",
      borderBottom: "1px solid #E0E0E0",
      backgroundColor: "#fff",
    },
    childTdLast: {
      borderRight: "none",
    },
    childTdLastRow: {
      borderBottom: "none",
    },
    childTdSpanned: {
      padding: "12px 10px",
      textAlign: "left",
      fontSize: "13px",
      color: "#333",
      verticalAlign: "top",
      borderRight: "1px solid #E0E0E0",
      borderBottom: "1px solid #E0E0E0",
      backgroundColor: "#fff",
    },
    subscriptionInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    },
    subscriptionName: {
      fontSize: "12px",
      fontWeight: 400,
      color: "#000",
    },
    subscriptionId: {
      fontSize: "12px",
      color: "#716F6D",
    },
    techRefId: {
      fontSize: "12px",
      color: "#000",
    },
    hardwareInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    },
    hardwareName: {
      fontSize: "12px",
      color: "#000",
    },
    hardwareId: {
      fontSize: "12px",
      color: "#716F6D",
    },
    expandIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "24px",
      height: "24px",
      fontSize: "14px",
      color: "#000",
      cursor: "pointer",
      flexShrink: 0,
    },
    noData: {
      padding: "24px",
      textAlign: "center",
      color: "#6F7171",
    },
    imageContainer: { display: "flex", gap: "6px" },
    shippingStatus: {
      padding: "2px 4px",
      fontWeight: 700,
      fontSize: "12px",
      lineHeight: "16px",
      color: "#FFFFFF",
      backgroundColor: "#008331",
      borderRadius: "4px",
      width: "fit-content",
    },
  };

  const toggleRowExpansion = (rowId, e) => {
    e.stopPropagation();
    setExpandedRows((prev) => {
      // If the clicked row is already expanded, collapse it
      if (prev[rowId]) {
        return {};
      }
      // Otherwise, expand only the clicked row (collapse all others)
      return { [rowId]: true };
    });
  };

  const getStatusStyle = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "shipped") return { ...styles.statusBadge, ...styles.statusShipped };
    if (statusLower === "never scheduled") return { ...styles.statusBadge, ...styles.statusNeverScheduled };
    return styles.statusBadge;
  };

  const renderExpandIcon = (isExpanded, rowId) => (
    <span
      style={styles.expandIcon}
      onClick={(e) => toggleRowExpansion(rowId, e)}
      role="button"
      aria-label={isExpanded ? "Collapse row" : "Expand row"}
      aria-expanded={isExpanded}
    >
      <i className={isExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"} />
    </span>
  );

  // Helper to get cell style based on position and expansion state
  const getTdStyle = (isLast, isExpanded) => ({
    ...styles.td,
    ...(isLast ? styles.tdLast : {}),
    ...(isExpanded ? styles.tdExpandedParent : {}),
  });

  const getChildTdStyle = (isLastCol, isLastRow, isLastMaterialInSubscription) => ({
    ...styles.childTd,
    ...(isLastCol ? styles.childTdLast : {}),
    ...(isLastRow ? styles.childTdLastRow : {}),
    // Remove bottom border if not the last material in the subscription
    ...(!isLastMaterialInSubscription && !isLastRow && { borderBottom: "none" }),
  });

  const getSpannedTdStyle = (isLastRowOfSubscription, isLastSubscription) => ({
    ...styles.childTdSpanned,
    ...(isLastRowOfSubscription && isLastSubscription ? { borderBottom: "none" } : {}),
  });

  const renderChildRows = (groupedSubscriptions) => {
    const rows = [];
    const totalSubscriptions = groupedSubscriptions.length;

    groupedSubscriptions.forEach((sub, subIndex) => {
      const isLastSubscription = subIndex === totalSubscriptions - 1;
      const materials = sub.materials || [];
      const materialCount = materials.length || 1;

      materials.forEach((material, matIndex) => {
        const isFirstMaterial = matIndex === 0;
        const isLastMaterial = matIndex === materials.length - 1;
        const isLastRow = isLastSubscription && isLastMaterial;
        const { address, address1, city, stateCode, country, zipCode } = material?.shippingAddress || {};
        const streetPart = [address, address1].filter(Boolean).join(", ");
        const cityPart = `${city ? `${city}, ` : ""}${stateCode || ""} ${zipCode || ""}${country ? `, ${country}` : ""}`;

        rows.push(
          <tr key={`${sub.subscriptionId}-${material.id || matIndex}`}>
            {/* Subscription Column - Only render on first material row, span all materials */}
            {isFirstMaterial && (
              <td style={getSpannedTdStyle(isLastMaterial, isLastSubscription)} rowSpan={materialCount}>
                <div style={styles.subscriptionInfo}>
                  <span style={styles.subscriptionName}>{sub.subscriptionName || "-"}</span>
                  {sub.subscriptionId && <span style={styles.subscriptionId}>{`(${sub.subscriptionId})`}</span>}
                  {sub.techRefId && (
                    <span style={styles.techRefId}>
                      <b>Tech-Ref ID:</b> {sub.techRefId}
                    </span>
                  )}
                </div>
              </td>
            )}

            {/* Category Column */}
            <td style={getChildTdStyle(false, isLastRow, isLastMaterial)}>
              <div style={styles.categoryInfo}>
                <span style={styles.categoryType}>{material.type || "-"}</span>
                {material.subType && <span style={styles.categorySubType}>{` - ${material.subType}`}</span>}
              </div>
            </td>

            {/* Hardware Column */}
            <td style={getChildTdStyle(false, isLastRow, isLastMaterial)}>
              <div style={styles.hardwareInfo}>
                <span style={styles.hardwareName}>{material.hardwareName || "-"}</span>
                {material.hardwareId && <span style={styles.hardwareId}>{material.hardwareId}</span>}
              </div>
            </td>

            {/* Shipping Address Column */}
            <td style={getChildTdStyle(false, isLastRow, isLastMaterial)}>
              <div style={styles.addressCell}>
                <span style={styles.addressText}>{material?.shippingAddress ? `${streetPart} ${cityPart}` : "--"}</span>
                {material?.shippingAddress?.contactName && (
                  <span style={styles.contactText}>
                    <b>Contact:</b> {material?.shippingAddress?.contactName}
                  </span>
                )}
              </div>
            </td>

            {/* Shipping Status Column */}
            <td style={getChildTdStyle(false, isLastRow, isLastMaterial)}>
              {material.shippingStatus ? (
                <>
                  <Badge>{material.shippingStatus}</Badge>
                </>
              ) : (
                <div>--</div>
              )}
            </td>

            {/* Install Status Column */}
            <td style={getChildTdStyle(true, isLastRow, isLastMaterial)}>
              <div style={styles.statusCell}>
                {material.installStatus ? <span style={getStatusStyle(material.installStatus)}>{material.installStatus}</span> : <span>--</span>}
              </div>
            </td>
          </tr>
        );
      });
    });

    return rows;
  };

  if (!data || data.length === 0) {
    return (
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.removeRightBorder, width: columnWidths.vehicle }}>Vehicle</th>
              <th style={{ ...styles.th, width: columnWidths.traits }}>Traits</th>
              <th style={{ ...styles.th, width: columnWidths.installDetails }}>Install Details</th>
              <th style={{ ...styles.th, width: columnWidths.shippingAddress }}>Shipping Address</th>
              <th style={{ ...styles.th, width: columnWidths.shippingStatus }}>Shipping Status</th>
              <th style={{ ...styles.th, ...styles.thLast, width: columnWidths.instStatus }}>Inst. Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={6} style={styles.noData}>
                No data available
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const DisplayInstallType = (installType) => {
    if (!installType) return null;
    const type = installType.toUpperCase();
    const isConverted = type === "VMIC" || type === "CMIC";
    const baseLabel = isConverted ? type.slice(0, -1) : type;
    return (
      <span style={styles.installTypeLabel}>
        {baseLabel + " "}
        {isConverted && <span style={{ color: "#ff8027" }}>(Converted)</span>}
      </span>
    );
  };

  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, width: columnWidths.vehicle, borderRight: "none" }}>{isVehicle ? "Vehicle" : "Asset"}</th>
            <th style={{ ...styles.th, width: columnWidths.traits, borderRight: "none" }}></th>
            <th style={{ ...styles.th, width: columnWidths.installDetails }}></th>
            <th style={{ ...styles.th, width: columnWidths.shippingAddress }}>Shipping Address</th>
            <th style={{ ...styles.th, width: columnWidths.shippingStatus }}>Shipping Status</th>
            <th style={{ ...styles.th, ...styles.thLast, width: columnWidths.instStatus }}>Inst. Status</th>
          </tr>
        </thead>
        <tbody>
          {formattedData.map((vehicle, index) => {
            const rowId = vehicle.id || index;
            const isExpanded = expandedRows[rowId];
            const hasSubscriptions = vehicle.formattedSubscriptions && vehicle.formattedSubscriptions.length > 0;
            const parts = [vehicle?.year, vehicle?.make, vehicle?.model];
            const vehicleNameStr = parts.every((val) => val != null) ? parts.join(" ") : "";
            const { address, address1, city, stateCode, country, zipCode } = vehicle?.shippingAddress || {};
            const streetPart = [address, address1].filter(Boolean).join(", ");
            const cityPart = `${city ? `${city}, ` : ""}${stateCode || ""} ${zipCode || ""}${country ? `, ${country}` : ""}`;

            return (
              <React.Fragment key={rowId}>
                {/* Parent Row */}
                <tr
                  style={{
                    ...styles.parentRow,
                    ...(isExpanded ? styles.parentRowExpanded : {}),
                  }}
                  onClick={(e) => hasSubscriptions && toggleRowExpansion(rowId, e)}
                  onMouseEnter={(e) => {
                    // if (hasSubscriptions) e.currentTarget.style.backgroundColor = "#F5F5F5";
                  }}
                  onMouseLeave={(e) => {
                    // if (hasSubscriptions) e.currentTarget.style.backgroundColor = isExpanded ? "#F9F9F9" : "#fff";
                  }}
                >
                  {/* Vehicle Column */}

                  <td style={{ ...getTdStyle(false, isExpanded), borderRight: "none" }}>
                    <div style={styles.vehicleCell}>
                      <div style={styles.imageContainer}>
                        <img
                          src={isVehicle ? VehicleIcon : AssetIcon}
                          alt="ondemand-icon"
                          width={34}
                          height={34}
                          style={{ filter: "brightness(0)" }}
                        />
                        <div>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            {isVehicle ? (
                              <span style={styles.vehicleName}>{vehicleNameStr}</span>
                            ) : (
                              <span style={styles.vehicleName}>Powered Asset</span>
                            )}
                            {DisplayInstallType(vehicle.installType)}
                          </div>
                        </div>
                      </div>
                      {isVehicle ? (
                        <div style={styles.installDetailRow}>
                          <span style={styles.installDetailTitle}>VIN: </span>
                          <span style={styles.installDetailValue}>{vehicle?.vehicle?.startsWith("VIN") ? "" : vehicle.vehicle}</span>
                        </div>
                      ) : null}
                      <div style={styles.installDetailRow}>
                        <span style={styles.installDetailTitle}>Name: </span>
                        <span style={styles.installDetailValue}>{vehicle.vehicleName || "--"}</span>
                      </div>
                      <div style={styles.installDetailRow}>
                        <span style={styles.installDetailTitle}>Licence Plate: </span>
                        <span style={styles.installDetailValue}>{isVehicle ? vehicle.licensePlate || "--" : "--"}</span>
                      </div>
                    </div>
                  </td>
                  {/* Traits Column */}
                  <td style={{ ...getTdStyle(false, isExpanded), borderRight: "none" }}>
                    <div style={styles.traitsCell}>
                      <span style={styles.traitsTitle}>Traits</span>
                      <span style={styles.traitsValue}>{vehicle.traits || "--"}</span>
                      <div style={styles.traitsRow}>
                        {vehicle.wheelChair && (
                          <IconWithTooltip
                            id={`wheelchair-icon-${vehicle.id}`}
                            iconName="international-symbol-of-access"
                            tooltipContent={TOOLTIP_MESSAGES.WHEELCHAIR}
                            tooltipPosition="top"
                          />
                        )}
                        {vehicle.emergency && (
                          <IconWithTooltip
                            id={`emergency-icon-${vehicle.id}`}
                            iconName="police-fleet"
                            tooltipContent={TOOLTIP_MESSAGES.EMERGENCY}
                            tooltipPosition="top"
                          />
                        )}
                        {vehicle.killSwitch && (
                          <IconWithTooltip
                            id={`killswitch-icon-${vehicle.id}`}
                            iconName="smart-switch"
                            tooltipContent={TOOLTIP_MESSAGES.KILLSWITCH}
                            tooltipPosition="top"
                          />
                        )}
                        {vehicle.isCompetitor && (
                          <IconWithTooltip
                            id={`competitor-device-${vehicle.id}`}
                            iconName="router"
                            tooltipContent={
                              vehicle.isCompetitorComplete ? TOOLTIP_MESSAGES.COMPETITOR_DEVICE_REMOVED : TOOLTIP_MESSAGES.COMPETITOR_DEVICE
                            }
                            tooltipPosition="top"
                            wrapperColor={vehicle.isCompetitorComplete ? COLORS.COMPETITOR_COMPLETE : COLORS.COMPETITOR_INCOMPLETE}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  {/* Install Details Column */}
                  <td style={getTdStyle(false, isExpanded)}>
                    <div style={styles.installDetails}>
                      <span style={styles.traitsTitle}>Install Details</span>
                      <div style={styles.installDetailRow}>
                        <span style={styles.installDetailTitle}>Est. Time: </span>
                        <span style={styles.installDetailValue}>{vehicle.estimatedInstallDuration || "--"}</span>
                      </div>
                      <div style={styles.installDetailRow}>
                        <span style={styles.installDetailTitle}>Level: </span>
                        <span style={styles.installDetailValue}> {vehicle.level || "--"}</span>
                      </div>
                    </div>
                  </td>
                  {/* Shipping Address Column */}
                  <td style={getTdStyle(false, isExpanded)}>
                    <div style={styles.addressCell}>
                      <span style={styles.addressText}>{`${streetPart} ${cityPart}`}</span>
                      {vehicle?.shippingAddress?.contactName ? (
                        <div style={styles.installDetailRow}>
                          <span style={styles.installDetailTitle}>Contact: </span>
                          <span style={styles.installDetailValue}>{vehicle.shippingAddress.contactName}</span>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </td>
                  {/* Shipping Status Column */}
                  <td style={getTdStyle(false, isExpanded)}>
                    <div style={styles.statusCell}>
                      {vehicle.shippingStatus ? <span style={styles.shippingStatus}>{vehicle.shippingStatus}</span> : "--"}

                      {vehicle.estDeliveryDate && (
                        <>
                          <span style={styles.deliveryDateTitle}>Est. Delivery Date:</span>
                          <span style={styles.deliveryDateValue}>{vehicle.estDeliveryDate || "--"}</span>
                        </>
                      )}
                    </div>
                  </td>
                  {/* Inst. Status Column - with expand icon at right corner */}
                  <td style={getTdStyle(true, isExpanded)}>
                    <div style={styles.lastColumnCell}>
                      <div style={styles.statusCell}>
                        <span style={getStatusStyle(vehicle.instStatus)}>{vehicle.instStatus || "--"}</span>
                      </div>
                      {hasSubscriptions && renderExpandIcon(isExpanded, rowId)}
                    </div>
                  </td>
                </tr>

                {/* Expanded Child Row - Subscriptions */}
                {isExpanded && hasSubscriptions && (
                  <tr>
                    <td colSpan={6} style={styles.childRowCell}>
                      <table style={styles.childTable}>
                        <thead>
                          <tr style={styles.childHeaderRow}>
                            <th style={{ ...styles.childTh, width: childColumnWidths.subscription }}>Subscription</th>
                            <th style={{ ...styles.childTh, width: childColumnWidths.category }}>Category</th>
                            <th style={{ ...styles.childTh, width: childColumnWidths.hardware }}>Hardware</th>
                            <th style={{ ...styles.childTh, width: childColumnWidths.shippingAddress }}>Shipping Address</th>
                            <th style={{ ...styles.childTh, width: childColumnWidths.substatus1 }}>Substatus</th>
                            <th style={{ ...styles.childTh, ...styles.childThLast, width: childColumnWidths.substatus2 }}>Substatus</th>
                          </tr>
                        </thead>
                        <tbody>{renderChildRows(vehicle.formattedSubscriptions)}</tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default InstallationDetailsTable;
